/**
 * 技能激活卡片组件
 *
 * 展示技能激活状态，包括：
 * - 技能名称和图标
 * - 技能描述
 * - 激活状态动画
 * - 自动消失功能（2-3秒后）
 * - 错误状态样式
 *
 * @module SkillActivationCard
 */

import React, { useEffect, useState, useMemo } from 'react';

// ==================== 类型定义 ====================

export interface SkillActivationData {
  skillName: string;
  description?: string;
  status: 'activating' | 'active' | 'error';
  error?: string;
  timestamp: string;
}

export interface SkillActivationCardProps {
  data: SkillActivationData;
  autoDismiss?: boolean;
  dismissAfter?: number; // milliseconds
  onDismiss?: () => void;
}

// ==================== 图标组件 ====================

const SkillIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
    />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ErrorIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ==================== 状态配置 ====================

const STATUS_CONFIG = {
  activating: {
    bg: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-500',
    textColor: 'text-blue-700',
    badge: {
      bg: 'bg-blue-100',
      text: '激活中',
      animate: true,
    },
  },
  active: {
    bg: 'bg-green-50 border-green-200',
    iconColor: 'text-green-500',
    textColor: 'text-green-700',
    badge: {
      bg: 'bg-green-100',
      text: '已激活',
      animate: false,
    },
  },
  error: {
    bg: 'bg-red-50 border-red-200',
    iconColor: 'text-red-500',
    textColor: 'text-red-700',
    badge: {
      bg: 'bg-red-100',
      text: '激活失败',
      animate: false,
    },
  },
};

// ==================== 主组件 ====================

export const SkillActivationCard: React.FC<SkillActivationCardProps> = ({
  data,
  autoDismiss = true,
  dismissAfter = 2500,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  const config = STATUS_CONFIG[data.status];

  // 检查用户是否偏好减少动画 - 使用 useMemo 避免重复计算
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // 自动消失
  useEffect(() => {
    if (autoDismiss && data.status === 'active') {
      // 如果用户偏好减少动画，直接隐藏
      const fadeDelay = prefersReducedMotion ? 0 : dismissAfter - 500;
      
      const fadeTimer = setTimeout(() => {
        if (prefersReducedMotion) {
          setIsVisible(false);
          onDismiss?.();
        } else {
          setIsFading(true);
        }
      }, fadeDelay);

      const dismissTimer = prefersReducedMotion ? undefined : setTimeout(() => {
        setIsVisible(false);
        onDismiss?.();
      }, dismissAfter);

      return () => {
        clearTimeout(fadeTimer);
        if (dismissTimer) clearTimeout(dismissTimer);
      };
    }
  }, [autoDismiss, data.status, dismissAfter, onDismiss, prefersReducedMotion]);

  if (!isVisible) return null;

  return (
    <div
      className={`
        border rounded-lg p-3 shadow-sm transition-all
        ${config.bg}
        ${prefersReducedMotion 
          ? '' 
          : `duration-150 ${isFading ? 'opacity-0 translate-y-[-10px]' : 'opacity-100 translate-y-0'}`}
      `}
      role="status"
      aria-live="polite"
      aria-label={`技能激活: ${data.skillName}, 状态: ${config.badge.text}`}
    >
      <div className="flex items-center gap-3">
        {/* 图标 */}
        <div className={`p-2 rounded-md ${config.badge.bg}`} aria-hidden="true">
          {data.status === 'activating' ? (
            <div className="relative">
              <SkillIcon className={`${config.iconColor} ${prefersReducedMotion ? '' : 'animate-pulse'}`} />
            </div>
          ) : data.status === 'active' ? (
            <CheckIcon className={config.iconColor} />
          ) : (
            <ErrorIcon className={config.iconColor} />
          )}
        </div>

        {/* 内容 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium text-sm ${config.textColor} truncate`}>
              {data.skillName}
            </span>
            <span
              className={`
                inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                ${config.badge.bg} ${config.textColor}
                ${config.badge.animate && !prefersReducedMotion ? 'animate-pulse' : ''}
              `}
              aria-label={`状态: ${config.badge.text}`}
            >
              {config.badge.text}
            </span>
          </div>
          {data.description && (
            <p className="text-xs text-gray-700 mt-0.5 truncate">
              {data.description}
            </p>
          )}
          {data.error && (
            <p className="text-xs text-red-600 mt-1" role="alert">
              {data.error}
            </p>
          )}
        </div>

        {/* 技能标识 */}
        <div className="flex-shrink-0">
          <span 
            className="inline-flex items-center px-2 py-1 rounded text-xs bg-paper-100 text-paper-800 font-medium"
            aria-label="技能类型"
          >
            技能
          </span>
        </div>
      </div>
    </div>
  );
};

export default SkillActivationCard;
